// src/components/AnalyticsDadosVeiculos.js
// =============================================================================
// Analytics de Abastecimento & Sem Parar — BLB System
// -----------------------------------------------------------------------------
// Tela de inteligência operacional que cruza ABASTECIMENTO e SEM PARAR com a
// FROTA e os MOTORISTAS. Os gráficos se montam conforme o filtro é ajustado
// (recorte por data/veículo/motorista é instantâneo, client-side).
//
// Carga via UMA rota agregada (2 queries no servidor), evitando o padrão N+1:
//   GET /api/vehicles/analytics/consolidado
//     → { veiculos, abastecimentos (com métricas), sem_parar, meta }
//
// Recursos:
//   • KPIs consolidados (combustível, litros, pedágio, eficiência, custo/km)
//   • Evolução mensal (combustível × Sem Parar × total) + PROJEÇÃO linear
//   • Ranking de veículos por custo total
//   • Scorecard de motoristas (eficiência + gasto)  ← cruzamento por motorista
//   • [IDEIA] Custo por KM rodado (TCO operacional por veículo)
//   • [IDEIA] Comparativo de postos (preço médio por litro)
//   • [IDEIA] Quadrante Eficiência × Custo/km (scatter de performance)
//   • [IDEIA] Detector de anomalias / possíveis fraudes
//   • [IDEIA] Projeção de gastos (regressão linear + tendência)
//   • Distribuição por combustível e por tipo de uso Sem Parar (donuts)
//   • Exportar relatório HTML (standalone) e planilha XLSX (multi-aba)
//
// Gráficos em SVG puro (sem dependência nova). Export XLSX via SheetJS.
// =============================================================================

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import api from '../services/api';
import {
  FaGasPump,
  FaRoad,
  FaMoneyBillWave,
  FaChartLine,
  FaTachometerAlt,
  FaTruck,
  FaUser,
  FaTrophy,
  FaExclamationTriangle,
  FaFileExport,
  FaFileExcel,
  FaFileCode,
  FaSpinner,
  FaSyncAlt,
  FaFilter,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaMapMarkerAlt,
  FaCrosshairs,
  FaLightbulb,
  FaTimes,
} from 'react-icons/fa';
import './AnalyticsDadosVeiculos.css';

// ─────────────────────────────────────────────────────────────────────────────
// Paleta (tons profundos, calibrados para fundo claro "Warm Linen")
// ─────────────────────────────────────────────────────────────────────────────
const COR = {
  teal: '#0d7377',
  fuel: '#d97706',
  toll: '#2563eb',
  estac: '#059669',
  plano: '#7c3aed',
  total: '#0f766e',
  danger: '#dc2626',
  warn: '#d97706',
  muted: '#64748b',
};

const PALETA_SERIE = ['#0d7377', '#2563eb', '#d97706', '#7c3aed', '#059669', '#dc2626', '#db2777', '#65a30d'];

// ─────────────────────────────────────────────────────────────────────────────
// Formatação
// ─────────────────────────────────────────────────────────────────────────────
const BRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const NUM = (v, casas = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
};

const KM = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${n.toLocaleString('pt-BR')} km` : '—';
};

const KML = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${NUM(n, 2)} km/L` : '—';
};

// Formato compacto para rótulos de eixo (ex.: 2441.87 → "2,4 mil")
const COMPACTO = (v) => {
  const n = Math.abs(Number(v) || 0);
  if (n >= 1_000_000) return `${NUM(n / 1_000_000, 1)} mi`;
  if (n >= 1_000) return `${NUM(n / 1_000, 1)} mil`;
  return NUM(n, 0);
};

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const monthKey = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key) => {
  if (!key) return '—';
  const [y, m] = key.split('-');
  return `${MESES[Number(m) - 1]}/${String(y).slice(2)}`;
};

const ymd = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
};

const driverKey = (nome) => String(nome || '').trim().toLowerCase() || '__sem__';

// Regressão linear simples (mínimos quadrados) → { a, b } para y = a*x + b
const regressaoLinear = (pts) => {
  const n = pts.length;
  if (n < 2) return null;
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  pts.forEach((p, i) => {
    sx += i; sy += p; sxy += i * p; sxx += i * i;
  });
  const denom = n * sxx - sx * sx;
  if (denom === 0) return null;
  const a = (n * sxy - sx * sy) / denom;
  const b = (sy - a * sx) / n;
  return { a, b };
};

// ─────────────────────────────────────────────────────────────────────────────
// GRÁFICOS — SVG puro
// =============================================================================

// Barras horizontais com rótulo de valor
function BarrasH({ dados, formato = BRL, max, altura = 26, gap = 12, mono = false }) {
  if (!dados || dados.length === 0) return <Vazio texto="Sem dados para o filtro atual." />;
  const maxVal = max ?? (Math.max(...dados.map((d) => d.valor), 0) || 1);
  const labelW = 172;
  const valW = 100;
  const h = dados.length * (altura + gap) + gap;
  const fit = (s) => { const t = String(s ?? ''); return t.length > 22 ? `${t.slice(0, 21)}…` : t; };
  return (
    <svg className="adv-svg" viewBox={`0 0 760 ${h}`} preserveAspectRatio="xMinYMin meet" role="img">
      {dados.map((d, i) => {
        const y = gap + i * (altura + gap);
        const w = Math.max(3, ((d.valor || 0) / maxVal) * (760 - labelW - valW - 16));
        return (
          <g key={d.key ?? i}>
            <text x={0} y={y + altura / 2 + 4} className={`adv-svg-label ${mono ? 'adv-svg-label--mono' : ''}`}>
              {fit(d.label)}
              <title>{d.label}</title>
            </text>
            <rect x={labelW} y={y} width={760 - labelW - valW - 16} height={altura} rx={7} className="adv-svg-track" />
            <rect x={labelW} y={y} width={w} height={altura} rx={7} fill={d.cor || COR.teal}>
              <title>{`${d.label}: ${formato(d.valor)}`}</title>
            </rect>
            <text x={760 - valW} y={y + altura / 2 + 4} className="adv-svg-value">
              {formato(d.valor)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Linhas / área para evolução temporal com série de projeção tracejada
function LinhasTempo({ pontos, series, projecaoIdx }) {
  if (!pontos || pontos.length === 0) return <Vazio texto="Sem histórico no período." />;
  const W = 760, H = 280, padL = 56, padR = 16, padT = 16, padB = 34;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxY = Math.max(
    1,
    ...pontos.flatMap((p) => series.map((s) => Number(p[s.campo]) || 0))
  );
  const xAt = (i) => padL + (pontos.length === 1 ? innerW / 2 : (i / (pontos.length - 1)) * innerW);
  const yAt = (v) => padT + innerH - (Math.max(0, v) / maxY) * innerH;
  const gridY = 4;

  const pathFor = (campo) =>
    pontos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(Number(p[campo]) || 0).toFixed(1)}`).join(' ');

  const areaFor = (campo) => {
    const top = pontos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(Number(p[campo]) || 0).toFixed(1)}`).join(' ');
    return `${top} L ${xAt(pontos.length - 1).toFixed(1)} ${yAt(0).toFixed(1)} L ${xAt(0).toFixed(1)} ${yAt(0).toFixed(1)} Z`;
  };

  return (
    <svg className="adv-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMin meet" role="img">
      {Array.from({ length: gridY + 1 }).map((_, g) => {
        const v = (maxY / gridY) * (gridY - g);
        const y = yAt(v);
        return (
          <g key={g}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} className="adv-svg-grid" />
            <text x={padL - 8} y={y + 4} className="adv-svg-axis adv-svg-axis--y" textAnchor="end">{COMPACTO(v)}</text>
          </g>
        );
      })}
      {series.find((s) => s.area) && (
        <path d={areaFor(series.find((s) => s.area).campo)} fill={`${series.find((s) => s.area).cor}22`} stroke="none" />
      )}
      {series.map((s) => {
        // separa traço sólido (histórico) de tracejado (projeção)
        if (projecaoIdx != null && s.projetavel) {
          const solidos = pontos.slice(0, projecaoIdx + 1);
          const tracejados = pontos.slice(projecaoIdx);
          const dSolido = solidos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(Number(p[s.campo]) || 0).toFixed(1)}`).join(' ');
          const dProj = tracejados.map((p, i) => {
            const realIdx = projecaoIdx + i;
            return `${i === 0 ? 'M' : 'L'} ${xAt(realIdx).toFixed(1)} ${yAt(Number(p[s.campo]) || 0).toFixed(1)}`;
          }).join(' ');
          return (
            <g key={s.campo}>
              <path d={dSolido} fill="none" stroke={s.cor} strokeWidth={2.4} />
              <path d={dProj} fill="none" stroke={s.cor} strokeWidth={2.4} strokeDasharray="6 5" opacity={0.85} />
            </g>
          );
        }
        return <path key={s.campo} d={pathFor(s.campo)} fill="none" stroke={s.cor} strokeWidth={s.area ? 2.6 : 1.8} opacity={s.area ? 1 : 0.85} />;
      })}
      {series.filter((s) => s.area).map((s) =>
        pontos.map((p, i) => (
          <circle key={`${s.campo}-${i}`} cx={xAt(i)} cy={yAt(Number(p[s.campo]) || 0)} r={3.2} fill={s.cor}>
            <title>{`${p.label} — ${s.nome}: ${BRL(p[s.campo])}`}</title>
          </circle>
        ))
      )}
      {pontos.map((p, i) => (
        <text key={`lbl-${i}`} x={xAt(i)} y={H - 12} className="adv-svg-axis" textAnchor="middle">{p.label}</text>
      ))}
    </svg>
  );
}

// Donut com legenda
function Donut({ dados, formato = BRL }) {
  const total = (dados || []).reduce((a, d) => a + (Number(d.valor) || 0), 0);
  if (!dados || dados.length === 0 || total <= 0) return <Vazio texto="Sem dados de distribuição." />;
  const cx = 90, cy = 90, r = 70, sw = 26;
  let acc = 0;
  const circ = 2 * Math.PI * r;
  return (
    <div className="adv-donut-wrap">
      <svg viewBox="0 0 180 180" className="adv-donut-svg" role="img">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth={sw} />
        {dados.map((d, i) => {
          const frac = (Number(d.valor) || 0) / total;
          const len = frac * circ;
          const dash = `${len} ${circ - len}`;
          const offset = circ * 0.25 - acc * circ;
          acc += frac;
          return (
            <circle
              key={d.label ?? i}
              cx={cx} cy={cy} r={r} fill="none"
              stroke={d.cor || PALETA_SERIE[i % PALETA_SERIE.length]}
              strokeWidth={sw} strokeDasharray={dash} strokeDashoffset={offset}
              transform={`rotate(-90 ${cx} ${cy})`}
            >
              <title>{`${d.label}: ${formato(d.valor)} (${(frac * 100).toFixed(1)}%)`}</title>
            </circle>
          );
        })}
        <text x={cx} y={cy - 4} className="adv-donut-total" textAnchor="middle">{formato(total)}</text>
        <text x={cx} y={cy + 14} className="adv-donut-cap" textAnchor="middle">total</text>
      </svg>
      <ul className="adv-donut-legend">
        {dados.map((d, i) => (
          <li key={d.label ?? i}>
            <span className="adv-dot" style={{ background: d.cor || PALETA_SERIE[i % PALETA_SERIE.length] }} />
            <span className="adv-donut-legend__label">{d.label}</span>
            <span className="adv-donut-legend__val">{formato(d.valor)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Scatter: Eficiência (y) × Custo por km (x), raio = gasto
function ScatterPerf({ pontos }) {
  if (!pontos || pontos.length === 0) return <Vazio texto="Sem veículos com KM e consumo suficientes." />;
  const W = 760, H = 320, padL = 56, padR = 18, padT = 18, padB = 40;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const maxX = Math.max(...pontos.map((p) => p.x), 0.01) * 1.1;
  const maxY = Math.max(...pontos.map((p) => p.y), 0.01) * 1.15;
  const maxG = Math.max(...pontos.map((p) => p.gasto), 1);
  const xAt = (v) => padL + (v / maxX) * innerW;
  const yAt = (v) => padT + innerH - (v / maxY) * innerH;
  const rAt = (g) => 5 + (g / maxG) * 16;
  const midX = xAt(maxX / 2), midY = yAt(maxY / 2);
  return (
    <svg className="adv-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMin meet" role="img">
      {/* quadrantes */}
      <rect x={padL} y={padT} width={midX - padL} height={midY - padT} className="adv-quad adv-quad--good" />
      <rect x={midX} y={midY} width={W - padR - midX} height={padT + innerH - midY} className="adv-quad adv-quad--bad" />
      <line x1={midX} y1={padT} x2={midX} y2={padT + innerH} className="adv-svg-grid" />
      <line x1={padL} y1={midY} x2={W - padR} y2={midY} className="adv-svg-grid" />
      <text x={padL + 6} y={padT + 14} className="adv-quad-tag adv-quad-tag--good">eficiente • econômico</text>
      <text x={W - padR - 6} y={padT + innerH - 6} className="adv-quad-tag adv-quad-tag--bad" textAnchor="end">ineficiente • caro</text>
      {pontos.map((p, i) => (
        <g key={p.key ?? i}>
          <circle cx={xAt(p.x)} cy={yAt(p.y)} r={rAt(p.gasto)} fill={`${p.cor || COR.teal}cc`} stroke={p.cor || COR.teal} strokeWidth={1.4}>
            <title>{`${p.label}\nCusto/km: ${BRL(p.x)}\nEficiência: ${KML(p.y)}\nGasto: ${BRL(p.gasto)}`}</title>
          </circle>
          <text x={xAt(p.x)} y={yAt(p.y) - rAt(p.gasto) - 3} className="adv-svg-axis" textAnchor="middle">{p.label}</text>
        </g>
      ))}
      <text x={W / 2} y={H - 8} className="adv-svg-axis" textAnchor="middle">Custo por km (R$) →</text>
      <text x={14} y={H / 2} className="adv-svg-axis" textAnchor="middle" transform={`rotate(-90 14 ${H / 2})`}>← Eficiência (km/L)</text>
    </svg>
  );
}

function Vazio({ texto }) {
  return <div className="adv-vazio">{texto}</div>;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function AnalyticsDadosVeiculos({ onClose }) {
  const [veiculos, setVeiculos] = useState([]);
  const [fuel, setFuel] = useState([]);   // abastecimentos achatados (com veículo + motorista)
  const [tags, setTags] = useState([]);   // lançamentos Sem Parar achatados
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  // filtros
  const [fVeiculo, setFVeiculo] = useState('');
  const [fMotorista, setFMotorista] = useState('');
  const [fCombustivel, setFCombustivel] = useState('');
  const [fIni, setFIni] = useState('');
  const [fFim, setFFim] = useState('');

  const relatorioRef = useRef(null);
  const abortRef = useRef(false);

  // ───────────────────────────────────────────────────────────────────────
  // Carga progressiva da frota
  // ───────────────────────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    abortRef.current = false;
    setCarregando(true);
    setErro(null);
    setFuel([]);
    setTags([]);

    try {
      const { data } = await api.get('/api/vehicles/analytics/consolidado');
      if (abortRef.current) return;

      setVeiculos(data?.veiculos || []);

      // Abastecimentos já vêm com métricas calculadas no servidor
      // (R$/L, distância, eficiência, alerta de odômetro) — idêntico a logs-uso.
      const abast = (data?.abastecimentos || []).map((a) => ({
        ...a,
        motorista_nome:
          a.motorista_nome && a.motorista_nome !== 'Não informado'
            ? a.motorista_nome
            : 'Não atribuído',
      }));
      setFuel(abast);

      setTags(data?.sem_parar || []);
    } catch (e) {
      if (!abortRef.current) {
        setErro('Não foi possível carregar os dados de analytics. Verifique sua conexão e tente recarregar.');
        toast.error('Falha ao carregar dados consolidados.');
      }
    } finally {
      if (!abortRef.current) setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    return () => { abortRef.current = true; };
  }, [carregar]);

  // ───────────────────────────────────────────────────────────────────────
  // Opções de filtro derivadas dos dados
  // ───────────────────────────────────────────────────────────────────────
  const opcoesMotorista = useMemo(() => {
    const m = new Map();
    fuel.forEach((f) => {
      const k = driverKey(f.motorista_nome);
      if (!m.has(k)) m.set(k, f.motorista_nome || 'Não atribuído');
    });
    return [...m.entries()].map(([key, nome]) => ({ key, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [fuel]);

  const opcoesCombustivel = useMemo(() => {
    const s = new Set();
    fuel.forEach((f) => { if (f.tipo_combustivel) s.add(f.tipo_combustivel); });
    return [...s].sort();
  }, [fuel]);

  // ───────────────────────────────────────────────────────────────────────
  // Aplicação de filtros
  // ───────────────────────────────────────────────────────────────────────
  const dentroPeriodo = useCallback((d) => {
    const dia = ymd(d);
    if (!dia) return false;
    if (fIni && dia < fIni) return false;
    if (fFim && dia > fFim) return false;
    return true;
  }, [fIni, fFim]);

  const fuelFiltrado = useMemo(() => fuel.filter((f) => {
    if (fVeiculo && String(f.veiculo_id) !== String(fVeiculo)) return false;
    if (fMotorista && driverKey(f.motorista_nome) !== fMotorista) return false;
    if (fCombustivel && f.tipo_combustivel !== fCombustivel) return false;
    return dentroPeriodo(f.data || f.data_abastecimento);
  }), [fuel, fVeiculo, fMotorista, fCombustivel, dentroPeriodo]);

  const tagsFiltrado = useMemo(() => tags.filter((t) => {
    if (fVeiculo && String(t.veiculo_id) !== String(fVeiculo)) return false;
    if (fMotorista && driverKey(t.motorista_nome) !== fMotorista) return false;
    return dentroPeriodo(t.data_transacao);
  }), [tags, fVeiculo, fMotorista, dentroPeriodo]);

  // ───────────────────────────────────────────────────────────────────────
  // Núcleo analítico
  // ───────────────────────────────────────────────────────────────────────
  const A = useMemo(() => {
    const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

    // ── KPIs base ──
    const totalCombustivel = fuelFiltrado.reduce((s, f) => s + num(f.valor), 0);
    const totalLitros = fuelFiltrado.reduce((s, f) => s + num(f.litros), 0);

    const tagDebito = tagsFiltrado.filter((t) => t.debito_credito !== 'CR');
    const totalSemParar = tagDebito.reduce((s, t) => s + num(t.valor), 0);
    const totalPedagio = tagDebito.filter((t) => t.tipo_uso === 'PASSAGENS').reduce((s, t) => s + num(t.valor), 0);
    const totalEstac = tagDebito.filter((t) => t.tipo_uso === 'ESTACIONAMENTO').reduce((s, t) => s + num(t.valor), 0);
    const totalPlano = tagDebito.filter((t) => t.tipo_uso === 'PLANO CONTRATADO').reduce((s, t) => s + num(t.valor), 0);

    const custoTotalGeral = totalCombustivel + totalSemParar;

    // distância + eficiência da frota (usando métricas já calculadas no backend)
    const distVal = fuelFiltrado.filter((f) => num(f.distancia_ultimo_abastecimento) > 0);
    const distTotal = distVal.reduce((s, f) => s + num(f.distancia_ultimo_abastecimento), 0);
    const litrosNoTrecho = distVal.reduce((s, f) => s + num(f.litros), 0);
    const eficienciaFrota = litrosNoTrecho > 0 ? distTotal / litrosNoTrecho : 0;
    const custoPorKmFrota = distTotal > 0 ? custoTotalGeral / distTotal : 0;

    // ── Por veículo ──
    const mapV = new Map();
    const ensureV = (id, placa, modelo) => {
      if (!mapV.has(id)) mapV.set(id, { veiculo_id: id, placa, modelo, gastoComb: 0, litros: 0, dist: 0, litrosTrecho: 0, gastoSP: 0, abast: 0 });
      return mapV.get(id);
    };
    fuelFiltrado.forEach((f) => {
      const r = ensureV(f.veiculo_id, f.placa, f.modelo);
      r.gastoComb += num(f.valor);
      r.litros += num(f.litros);
      r.abast += 1;
      if (num(f.distancia_ultimo_abastecimento) > 0) {
        r.dist += num(f.distancia_ultimo_abastecimento);
        r.litrosTrecho += num(f.litros);
      }
    });
    tagDebito.forEach((t) => {
      const r = ensureV(t.veiculo_id, t.placa, t.modelo);
      r.gastoSP += num(t.valor);
    });
    const porVeiculo = [...mapV.values()].map((r) => ({
      ...r,
      custoTotal: r.gastoComb + r.gastoSP,
      eficiencia: r.litrosTrecho > 0 ? r.dist / r.litrosTrecho : 0,
      custoPorKm: r.dist > 0 ? (r.gastoComb + r.gastoSP) / r.dist : 0,
      precoMedioLitro: r.litros > 0 ? r.gastoComb / r.litros : 0,
    })).sort((a, b) => b.custoTotal - a.custoTotal);

    // ── Por motorista (cruzamento — combustível + Sem Parar) ──
    const mapM = new Map();
    const ensureM = (nome) => {
      const k = driverKey(nome);
      if (!mapM.has(k)) mapM.set(k, { key: k, nome: nome || 'Não atribuído', gastoComb: 0, gastoSP: 0, litros: 0, dist: 0, litrosTrecho: 0, abast: 0, estimados: 0, veiculos: new Set() });
      return mapM.get(k);
    };
    fuelFiltrado.forEach((f) => {
      const r = ensureM(f.motorista_nome);
      r.gastoComb += num(f.valor);
      r.litros += num(f.litros);
      r.abast += 1;
      r.veiculos.add(f.veiculo_id);
      if (f.motorista_por_veiculo) r.estimados += 1;
      if (num(f.distancia_ultimo_abastecimento) > 0) {
        r.dist += num(f.distancia_ultimo_abastecimento);
        r.litrosTrecho += num(f.litros);
      }
    });
    tagDebito.forEach((t) => {
      const r = ensureM(t.motorista_nome);
      r.gastoSP += num(t.valor);
      r.veiculos.add(t.veiculo_id);
      if (t.motorista_por_veiculo) r.estimados += 1;
    });
    const porMotorista = [...mapM.values()].map((r) => ({
      key: r.key,
      nome: r.nome,
      gastoComb: r.gastoComb,
      gastoSP: r.gastoSP,
      gastoTotal: r.gastoComb + r.gastoSP,
      litros: r.litros,
      abast: r.abast,
      estimados: r.estimados,
      numVeiculos: r.veiculos.size,
      eficiencia: r.litrosTrecho > 0 ? r.dist / r.litrosTrecho : 0,
      custoPorKm: r.dist > 0 ? r.gastoComb / r.dist : 0,
    })).sort((a, b) => b.gastoTotal - a.gastoTotal);

    // ── Postos ──
    const mapP = new Map();
    fuelFiltrado.forEach((f) => {
      const nome = (f.posto || '').trim() || 'Não informado';
      if (!mapP.has(nome)) mapP.set(nome, { posto: nome, gasto: 0, litros: 0, count: 0 });
      const r = mapP.get(nome);
      r.gasto += num(f.valor);
      r.litros += num(f.litros);
      r.count += 1;
    });
    const postos = [...mapP.values()]
      .filter((p) => p.posto !== 'Não informado' || mapP.size === 1)
      .map((p) => ({ ...p, precoMedioLitro: p.litros > 0 ? p.gasto / p.litros : 0 }))
      .sort((a, b) => b.gasto - a.gasto);

    // ── Tipo de combustível ──
    const mapTC = new Map();
    fuelFiltrado.forEach((f) => {
      const nome = (f.tipo_combustivel || '').trim() || 'Não informado';
      if (!mapTC.has(nome)) mapTC.set(nome, { label: nome, valor: 0, litros: 0, count: 0 });
      const r = mapTC.get(nome);
      r.valor += num(f.valor);
      r.litros += num(f.litros);
      r.count += 1;
    });
    const tipoCombustivel = [...mapTC.values()].map((d, i) => ({ ...d, cor: PALETA_SERIE[i % PALETA_SERIE.length] })).sort((a, b) => b.valor - a.valor);

    // ── Tipo de uso Sem Parar ──
    const usoCores = { PASSAGENS: COR.toll, ESTACIONAMENTO: COR.estac, 'PLANO CONTRATADO': COR.plano, ABASTECIMENTO: COR.fuel };
    const mapTU = new Map();
    tagDebito.forEach((t) => {
      const nome = t.tipo_uso || 'OUTROS';
      if (!mapTU.has(nome)) mapTU.set(nome, { label: nome, valor: 0, count: 0 });
      const r = mapTU.get(nome);
      r.valor += num(t.valor);
      r.count += 1;
    });
    const tipoUsoSP = [...mapTU.values()].map((d, i) => ({ ...d, cor: usoCores[d.label] || PALETA_SERIE[i % PALETA_SERIE.length] })).sort((a, b) => b.valor - a.valor);

    // ── Evolução mensal + projeção ──
    const mapMes = new Map();
    fuelFiltrado.forEach((f) => {
      const k = monthKey(f.data || f.data_abastecimento);
      if (!k) return;
      if (!mapMes.has(k)) mapMes.set(k, { key: k, combustivel: 0, semParar: 0 });
      mapMes.get(k).combustivel += num(f.valor);
    });
    tagDebito.forEach((t) => {
      const k = monthKey(t.data_transacao);
      if (!k) return;
      if (!mapMes.has(k)) mapMes.set(k, { key: k, combustivel: 0, semParar: 0 });
      mapMes.get(k).semParar += num(t.valor);
    });
    let mensal = [...mapMes.values()]
      .map((m) => ({ ...m, total: m.combustivel + m.semParar, label: monthLabel(m.key) }))
      .sort((a, b) => a.key.localeCompare(b.key));

    // projeção: 1 mês à frente via regressão sobre o total
    let forecast = null;
    let projecaoIdx = null;
    if (mensal.length >= 3) {
      const reg = regressaoLinear(mensal.map((m) => m.total));
      if (reg) {
        const nextIdx = mensal.length;
        const proj = Math.max(0, reg.a * nextIdx + reg.b);
        const [y, mm] = mensal[mensal.length - 1].key.split('-').map(Number);
        const d = new Date(y, mm - 1 + 1, 1);
        const nextKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        projecaoIdx = mensal.length - 1;
        mensal = [...mensal, { key: nextKey, label: monthLabel(nextKey), combustivel: null, semParar: null, total: proj, projetado: true }];
        const media = mensal.slice(0, -1).reduce((s, m) => s + m.total, 0) / (mensal.length - 1);
        forecast = { valor: proj, label: monthLabel(nextKey), tendencia: reg.a, vsMedia: media > 0 ? (proj - media) / media : 0 };
      }
    }

    // ── Anomalias ──
    const anomalias = [];
    // odômetro regressivo (alerta do backend)
    fuelFiltrado.forEach((f) => {
      if (f.alerta_metrica) {
        anomalias.push({ tipo: 'odometro', sev: 'alta', placa: f.placa, data: f.data, valor: num(f.valor), motorista: f.motorista_nome, detalhe: f.alerta_metrica });
      }
    });
    // preço por litro fora da curva (z-score por tipo de combustível)
    const porTipo = new Map();
    fuelFiltrado.forEach((f) => {
      const ppl = num(f.valor_por_litro);
      if (ppl <= 0) return;
      const t = (f.tipo_combustivel || 'geral').trim();
      if (!porTipo.has(t)) porTipo.set(t, []);
      porTipo.get(t).push({ f, ppl });
    });
    porTipo.forEach((arr) => {
      if (arr.length < 4) return;
      const media = arr.reduce((s, x) => s + x.ppl, 0) / arr.length;
      const dp = Math.sqrt(arr.reduce((s, x) => s + (x.ppl - media) ** 2, 0) / arr.length) || 0;
      if (dp <= 0) return;
      arr.forEach(({ f, ppl }) => {
        const z = (ppl - media) / dp;
        if (z > 2.2) {
          anomalias.push({ tipo: 'preco', sev: 'media', placa: f.placa, data: f.data, valor: num(f.valor), motorista: f.motorista_nome, detalhe: `R$/L de ${BRL(ppl)} — ${NUM(z, 1)}σ acima da média (${BRL(media)}) p/ ${f.tipo_combustivel || 'combustível'}.` });
        }
      });
    });
    // eficiência improvável
    fuelFiltrado.forEach((f) => {
      const e = num(f.eficiencia_km_l);
      if (e > 0 && (e < 1.2 || e > 28)) {
        anomalias.push({ tipo: 'eficiencia', sev: e < 1.2 ? 'alta' : 'media', placa: f.placa, data: f.data, valor: num(f.valor), motorista: f.motorista_nome, detalhe: `Consumo registrado de ${KML(e)} — fora da faixa plausível (verificar odômetro/litros).` });
      }
    });
    anomalias.sort((a, b) => new Date(b.data) - new Date(a.data));

    // ── Scatter eficiência × custo/km ──
    const scatter = porVeiculo
      .filter((v) => v.eficiencia > 0 && v.custoPorKm > 0)
      .map((v, i) => ({ key: v.veiculo_id, label: v.placa || v.modelo, x: v.custoPorKm, y: v.eficiencia, gasto: v.custoTotal, cor: PALETA_SERIE[i % PALETA_SERIE.length] }));

    return {
      kpis: {
        totalCombustivel, totalLitros, totalSemParar, totalPedagio, totalEstac, totalPlano,
        custoTotalGeral, eficienciaFrota, custoPorKmFrota, distTotal,
        numVeiculos: porVeiculo.length, numMotoristas: porMotorista.filter((m) => m.key !== '__sem__').length,
        numAbastecimentos: fuelFiltrado.length, numLancamentosSP: tagsFiltrado.length,
      },
      porVeiculo, porMotorista, postos, tipoCombustivel, tipoUsoSP, mensal, forecast, projecaoIdx, anomalias, scatter,
    };
  }, [fuelFiltrado, tagsFiltrado]);

  const temDados = fuel.length > 0 || tags.length > 0;

  // ───────────────────────────────────────────────────────────────────────
  // Export — Planilha XLSX (multi-aba)
  // ───────────────────────────────────────────────────────────────────────
  const exportarXLSX = useCallback(() => {
    try {
      const wb = XLSX.utils.book_new();
      const k = A.kpis;

      const resumo = [
        ['Relatório', 'Analytics de Abastecimento & Sem Parar — BLB System'],
        ['Gerado em', new Date().toLocaleString('pt-BR')],
        ['Período', `${fIni || 'início'} a ${fFim || 'hoje'}`],
        [],
        ['Indicador', 'Valor'],
        ['Custo total (combustível + Sem Parar)', k.custoTotalGeral],
        ['Gasto com combustível', k.totalCombustivel],
        ['Litros abastecidos', k.totalLitros],
        ['Gasto Sem Parar', k.totalSemParar],
        ['  Pedágios', k.totalPedagio],
        ['  Estacionamentos', k.totalEstac],
        ['  Plano contratado', k.totalPlano],
        ['Distância medida (km)', k.distTotal],
        ['Eficiência média da frota (km/L)', Number(k.eficienciaFrota.toFixed(2))],
        ['Custo médio por km (R$)', Number(k.custoPorKmFrota.toFixed(2))],
        ['Veículos com dados', k.numVeiculos],
        ['Motoristas identificados', k.numMotoristas],
        ['Abastecimentos analisados', k.numAbastecimentos],
        ['Lançamentos Sem Parar', k.numLancamentosSP],
      ];
      if (A.forecast) resumo.push([`Projeção de gasto (${A.forecast.label})`, Number(A.forecast.valor.toFixed(2))]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumo), 'Resumo');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(A.porVeiculo.map((v) => ({
        Placa: v.placa, Modelo: v.modelo,
        'Gasto combustível': Number(v.gastoComb.toFixed(2)),
        'Litros': Number(v.litros.toFixed(2)),
        'Gasto Sem Parar': Number(v.gastoSP.toFixed(2)),
        'Custo total': Number(v.custoTotal.toFixed(2)),
        'Distância (km)': v.dist,
        'Eficiência (km/L)': v.eficiencia ? Number(v.eficiencia.toFixed(2)) : '',
        'Custo por km (R$)': v.custoPorKm ? Number(v.custoPorKm.toFixed(2)) : '',
        'Preço médio R$/L': v.precoMedioLitro ? Number(v.precoMedioLitro.toFixed(2)) : '',
        'Nº abastecimentos': v.abast,
      }))), 'Por Veículo');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(A.porMotorista.map((m) => ({
        Motorista: m.nome,
        'Gasto combustível': Number(m.gastoComb.toFixed(2)),
        'Gasto Sem Parar': Number(m.gastoSP.toFixed(2)),
        'Gasto total': Number(m.gastoTotal.toFixed(2)),
        'Litros': Number(m.litros.toFixed(2)),
        'Eficiência (km/L)': m.eficiencia ? Number(m.eficiencia.toFixed(2)) : '',
        'Custo por km (R$)': m.custoPorKm ? Number(m.custoPorKm.toFixed(2)) : '',
        'Nº abastecimentos': m.abast,
        'Veículos usados': m.numVeiculos,
        'Lançamentos estimados (p/ veículo)': m.estimados,
      }))), 'Por Motorista');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(A.postos.map((p) => ({
        Posto: p.posto,
        'Gasto total': Number(p.gasto.toFixed(2)),
        'Litros': Number(p.litros.toFixed(2)),
        'Preço médio R$/L': Number(p.precoMedioLitro.toFixed(2)),
        'Nº abastecimentos': p.count,
      }))), 'Postos');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(A.mensal.map((m) => ({
        'Mês': m.label,
        'Combustível': m.combustivel == null ? '' : Number(m.combustivel.toFixed(2)),
        'Sem Parar': m.semParar == null ? '' : Number(m.semParar.toFixed(2)),
        'Total': Number(m.total.toFixed(2)),
        'Projetado': m.projetado ? 'Sim' : '',
      }))), 'Evolução Mensal');

      if (A.anomalias.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(A.anomalias.map((an) => ({
          Tipo: an.tipo, Severidade: an.sev, Placa: an.placa,
          Data: ymd(an.data), Motorista: an.motorista, Valor: Number(an.valor.toFixed(2)), Detalhe: an.detalhe,
        }))), 'Anomalias');
      }

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fuelFiltrado.map((f) => ({
        Placa: f.placa, Data: ymd(f.data || f.data_abastecimento), Motorista: f.motorista_nome,
        Litros: Number(f.litros) || 0, Valor: Number(f.valor) || 0,
        'R$/L': f.valor_por_litro || '', Odômetro: f.odometro_registrado || '',
        'Distância (km)': f.distancia_ultimo_abastecimento || '', 'Eficiência (km/L)': f.eficiencia_km_l || '',
        Combustível: f.tipo_combustivel || '', Posto: f.posto || '', Origem: f.origem || '',
      }))), 'Abastecimentos');

      if (tagsFiltrado.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tagsFiltrado.map((t) => ({
          Placa: t.placa, 'Data/hora': t.data_transacao, 'Tipo de uso': t.tipo_uso,
          Descrição: t.descricao || '', Valor: Number(t.valor) || 0, 'DB/CR': t.debito_credito,
          Sentido: t.sentido || '', Fatura: t.numero_fatura || '',
        }))), 'Sem Parar');
      }

      const nome = `analytics_frota_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, nome);
      toast.success('Planilha exportada!');
    } catch (e) {
      toast.error('Falha ao exportar planilha.');
    }
  }, [A, fuelFiltrado, tagsFiltrado, fIni, fFim]);

  // ───────────────────────────────────────────────────────────────────────
  // Export — Relatório HTML standalone
  // ───────────────────────────────────────────────────────────────────────
  const exportarHTML = useCallback(() => {
    try {
      const k = A.kpis;
      const barra = (dados, fmt = BRL) => {
        const max = Math.max(...dados.map((d) => d.valor), 1);
        return dados.map((d) => `
          <div class="r-bar"><span class="r-bar__lbl">${d.label}</span>
            <span class="r-bar__track"><span class="r-bar__fill" style="width:${Math.max(2, (d.valor / max) * 100)}%;background:${d.cor || '#0d7377'}"></span></span>
            <span class="r-bar__val">${fmt(d.valor)}</span></div>`).join('');
      };
      const tabela = (cabs, linhas) => `
        <table class="r-tbl"><thead><tr>${cabs.map((c) => `<th>${c}</th>`).join('')}</tr></thead>
        <tbody>${linhas.map((l) => `<tr>${l.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table>`;

      const kpiCard = (lbl, val, cor) => `<div class="r-kpi"><span class="r-kpi__lbl">${lbl}</span><strong class="r-kpi__val" style="color:${cor || '#0f172a'}">${val}</strong></div>`;

      const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Analytics de Frota — BLB System</title>
<style>
  *{box-sizing:border-box}
  body{margin:0;font-family:'DM Sans',-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;background:#f1f5f4;padding:32px}
  .r-wrap{max-width:1040px;margin:0 auto}
  h1{font-size:1.7rem;margin:0 0 4px;color:#0d7377}
  .r-sub{color:#64748b;margin:0 0 24px;font-size:.9rem}
  h2{font-size:1.05rem;margin:32px 0 12px;padding-bottom:6px;border-bottom:2px solid #0d7377;color:#0f172a}
  .r-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px}
  .r-kpi{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:14px 16px}
  .r-kpi__lbl{display:block;font-size:.72rem;text-transform:uppercase;letter-spacing:.04em;color:#64748b;margin-bottom:6px}
  .r-kpi__val{font-size:1.35rem;font-weight:700}
  .r-bar{display:flex;align-items:center;gap:12px;margin:7px 0;font-size:.85rem}
  .r-bar__lbl{flex:0 0 160px;color:#334155}
  .r-bar__track{flex:1;height:18px;background:#e2e8f0;border-radius:6px;overflow:hidden}
  .r-bar__fill{display:block;height:100%;border-radius:6px}
  .r-bar__val{flex:0 0 110px;text-align:right;font-variant-numeric:tabular-nums;font-weight:600}
  .r-tbl{width:100%;border-collapse:collapse;font-size:.82rem;background:#fff;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0}
  .r-tbl th{background:#0d7377;color:#fff;text-align:left;padding:9px 12px;font-weight:600}
  .r-tbl td{padding:8px 12px;border-top:1px solid #eef2f1;font-variant-numeric:tabular-nums}
  .r-tbl tr:nth-child(even) td{background:#f8fafc}
  .r-anom{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:10px 14px;margin:7px 0;font-size:.84rem}
  .r-anom b{color:#c2410c}
  footer{margin-top:40px;color:#94a3b8;font-size:.75rem;text-align:center}
</style></head><body><div class="r-wrap">
  <h1>Analytics de Abastecimento &amp; Sem Parar</h1>
  <p class="r-sub">BLB System · gerado em ${new Date().toLocaleString('pt-BR')} · período: ${fIni || 'início'} → ${fFim || 'hoje'}</p>

  <h2>Indicadores consolidados</h2>
  <div class="r-kpis">
    ${kpiCard('Custo total', BRL(k.custoTotalGeral), '#0d7377')}
    ${kpiCard('Combustível', BRL(k.totalCombustivel), '#b45309')}
    ${kpiCard('Litros', `${NUM(k.totalLitros)} L`)}
    ${kpiCard('Sem Parar', BRL(k.totalSemParar), '#2563eb')}
    ${kpiCard('Eficiência média', KML(k.eficienciaFrota))}
    ${kpiCard('Custo por km', BRL(k.custoPorKmFrota))}
    ${A.forecast ? kpiCard(`Projeção ${A.forecast.label}`, BRL(A.forecast.valor), '#7c3aed') : ''}
  </div>

  <h2>Ranking de veículos por custo total</h2>
  ${barra(A.porVeiculo.slice(0, 12).map((v) => ({ label: `${v.placa || v.modelo}`, valor: v.custoTotal, cor: '#0d7377' })))}

  <h2>Motoristas — gasto total (combustível + Sem Parar)</h2>
  ${barra(A.porMotorista.slice(0, 12).map((m) => ({ label: m.nome, valor: m.gastoTotal, cor: '#2563eb' })))}

  <h2>Custo por km rodado (veículos com KM)</h2>
  ${A.porVeiculo.filter((v) => v.custoPorKm > 0).length
        ? tabela(['Placa', 'Modelo', 'Custo/km', 'Eficiência', 'Distância'],
          A.porVeiculo.filter((v) => v.custoPorKm > 0).sort((a, b) => b.custoPorKm - a.custoPorKm).slice(0, 15)
            .map((v) => [v.placa, v.modelo, BRL(v.custoPorKm), KML(v.eficiencia), KM(v.dist)]))
        : '<p class="r-sub">Sem dados de odômetro suficientes.</p>'}

  <h2>Comparativo de postos</h2>
  ${A.postos.length
        ? tabela(['Posto', 'Gasto', 'Litros', 'Preço médio R$/L', 'Abast.'],
          A.postos.slice(0, 15).map((p) => [p.posto, BRL(p.gasto), NUM(p.litros), BRL(p.precoMedioLitro), p.count]))
        : '<p class="r-sub">Sem postos identificados.</p>'}

  <h2>Evolução mensal</h2>
  ${tabela(['Mês', 'Combustível', 'Sem Parar', 'Total'],
        A.mensal.map((m) => [m.label + (m.projetado ? ' (proj.)' : ''), m.combustivel == null ? '—' : BRL(m.combustivel), m.semParar == null ? '—' : BRL(m.semParar), BRL(m.total)]))}

  <h2>Anomalias detectadas (${A.anomalias.length})</h2>
  ${A.anomalias.length
        ? A.anomalias.slice(0, 30).map((an) => `<div class="r-anom"><b>[${an.sev.toUpperCase()}] ${an.placa}</b> · ${ymd(an.data)} · ${an.motorista} — ${an.detalhe}</div>`).join('')
        : '<p class="r-sub">Nenhuma anomalia encontrada no período. 🎉</p>'}

  <footer>Relatório gerado automaticamente pelo BLB System · dados sujeitos a conferência.</footer>
</div></body></html>`;

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_frota_${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Relatório HTML exportado!');
    } catch (e) {
      toast.error('Falha ao exportar relatório HTML.');
    }
  }, [A, fIni, fFim]);

  const limparFiltros = () => { setFVeiculo(''); setFMotorista(''); setFCombustivel(''); setFIni(''); setFFim(''); };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────
  const f = A.forecast;
  const tendIcon = f ? (f.tendencia > 0.5 ? <FaArrowUp /> : f.tendencia < -0.5 ? <FaArrowDown /> : <FaMinus />) : null;
  const tendClasse = f ? (f.tendencia > 0.5 ? 'sobe' : f.tendencia < -0.5 ? 'desce' : 'estavel') : '';

  return (
    <div className="adv-page">
      {/* ── Header ── */}
      <header className="adv-header">
        <div className="adv-header__title">
          <div className="adv-header__icon"><FaChartLine /></div>
          <div>
            <h1>Analytics de Frota</h1>
            <p>Abastecimento &amp; Sem Parar cruzados por veículo e motorista</p>
          </div>
        </div>
        <div className="adv-header__actions">
          <button type="button" className="adv-btn adv-btn--ghost" onClick={carregar} disabled={carregando} title="Recarregar dados">
            {carregando ? <FaSpinner className="adv-spin" /> : <FaSyncAlt />} Atualizar
          </button>
          <button type="button" className="adv-btn adv-btn--excel" onClick={exportarXLSX} disabled={!temDados}>
            <FaFileExcel /> Planilha
          </button>
          <button type="button" className="adv-btn adv-btn--html" onClick={exportarHTML} disabled={!temDados}>
            <FaFileCode /> Relatório HTML
          </button>
          {onClose && (
            <button type="button" className="adv-btn adv-btn--ghost adv-btn--icon" onClick={onClose} title="Fechar"><FaTimes /></button>
          )}
        </div>
      </header>

      {/* ── Carregamento (rota consolidada — 1 requisição) ── */}
      {carregando && (
        <div className="adv-progress">
          <div className="adv-progress__bar adv-progress__bar--indeterminate"><span /></div>
          <span className="adv-progress__txt">Carregando dados consolidados…</span>
        </div>
      )}

      {/* ── Filtros ── */}
      <section className="adv-filtros">
        <div className="adv-filtros__icon"><FaFilter /></div>
        <div className="adv-filtro">
          <label>Veículo</label>
          <select value={fVeiculo} onChange={(e) => setFVeiculo(e.target.value)}>
            <option value="">Todos</option>
            {veiculos.map((v) => <option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>)}
          </select>
        </div>
        <div className="adv-filtro">
          <label>Motorista</label>
          <select value={fMotorista} onChange={(e) => setFMotorista(e.target.value)}>
            <option value="">Todos</option>
            {opcoesMotorista.map((m) => <option key={m.key} value={m.key}>{m.nome}</option>)}
          </select>
        </div>
        <div className="adv-filtro">
          <label>Combustível</label>
          <select value={fCombustivel} onChange={(e) => setFCombustivel(e.target.value)}>
            <option value="">Todos</option>
            {opcoesCombustivel.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="adv-filtro">
          <label>De</label>
          <input type="date" value={fIni} onChange={(e) => setFIni(e.target.value)} />
        </div>
        <div className="adv-filtro">
          <label>Até</label>
          <input type="date" value={fFim} onChange={(e) => setFFim(e.target.value)} />
        </div>
        <button type="button" className="adv-btn adv-btn--ghost adv-filtro__clear" onClick={limparFiltros}>Limpar</button>
      </section>

      {erro && <div className="adv-erro">{erro}</div>}

      {!temDados && !carregando && !erro && (
        <div className="adv-empty-state">
          <FaGasPump />
          <h3>Nenhum dado de abastecimento ou Sem Parar encontrado</h3>
          <p>Importe extratos pelo menu <strong>Ações → Abastecimentos</strong> ou <strong>Ações → Sem Parar</strong> na ficha de cada veículo. Esta tela se monta sozinha conforme os dados chegam.</p>
        </div>
      )}

      {temDados && (
        <div className="adv-content" ref={relatorioRef}>

          {/* ── KPIs ── */}
          <section className="adv-kpis">
            <KpiCard icon={<FaMoneyBillWave />} cor={COR.teal} label="Custo total" valor={BRL(A.kpis.custoTotalGeral)} sub="combustível + Sem Parar" />
            <KpiCard icon={<FaGasPump />} cor={COR.fuel} label="Combustível" valor={BRL(A.kpis.totalCombustivel)} sub={`${NUM(A.kpis.totalLitros)} L · ${A.kpis.numAbastecimentos} abast.`} />
            <KpiCard icon={<FaRoad />} cor={COR.toll} label="Sem Parar" valor={BRL(A.kpis.totalSemParar)} sub={`pedágio ${BRL(A.kpis.totalPedagio)}`} />
            <KpiCard icon={<FaChartLine />} cor={COR.estac} label="Eficiência média" valor={KML(A.kpis.eficienciaFrota)} sub={`${KM(A.kpis.distTotal)} medidos`} />
            <KpiCard icon={<FaTachometerAlt />} cor={COR.plano} label="Custo por km" valor={A.kpis.custoPorKmFrota > 0 ? BRL(A.kpis.custoPorKmFrota) : '—'} sub="combustível + pedágio / km" />
            <KpiCard icon={<FaUser />} cor={COR.total} label="Cobertura" valor={`${A.kpis.numVeiculos} veíc.`} sub={`${A.kpis.numMotoristas} motoristas`} />
          </section>

          {/* ── Evolução mensal + projeção ── */}
          {A.mensal.length > 0 && (
            <Painel
              titulo="Evolução mensal & projeção"
              subtitulo="Combustível, Sem Parar e total ao longo do tempo. A linha tracejada é a projeção do próximo mês."
              badge={f && (
                <span className={`adv-forecast-badge adv-forecast-badge--${tendClasse}`}>
                  {tendIcon} Projeção {f.label}: <strong>{BRL(f.valor)}</strong>
                </span>
              )}
            >
              <LinhasTempo
                pontos={A.mensal}
                projecaoIdx={A.projecaoIdx}
                series={[
                  { campo: 'total', nome: 'Total', cor: COR.total, area: true, projetavel: true },
                  { campo: 'combustivel', nome: 'Combustível', cor: COR.fuel },
                  { campo: 'semParar', nome: 'Sem Parar', cor: COR.toll },
                ]}
              />
              <Legenda itens={[
                { cor: COR.total, txt: 'Total' },
                { cor: COR.fuel, txt: 'Combustível' },
                { cor: COR.toll, txt: 'Sem Parar' },
              ]} />
            </Painel>
          )}

          <div className="adv-grid-2">
            {/* ── Ranking veículos ── */}
            <Painel titulo="Ranking de veículos" subtitulo="Por custo total (combustível + Sem Parar)" icon={<FaTrophy />}>
              <BarrasH mono dados={A.porVeiculo.slice(0, 10).map((v) => ({ key: v.veiculo_id, label: v.placa || v.modelo, valor: v.custoTotal, cor: COR.teal }))} />
            </Painel>

            {/* ── Scorecard motoristas ── */}
            <Painel titulo="Motoristas" subtitulo="Cruzamento por condutor — gasto total (combustível + Sem Parar) e eficiência" icon={<FaUser />}>
              {A.porMotorista.length === 0 ? <Vazio texto="Sem motorista atribuído. Defina o motorista fixo na ficha de cada veículo." /> : (
                <>
                  <div className="adv-scroll">
                    <table className="adv-tbl">
                      <thead><tr><th>Motorista</th><th>Gasto total</th><th>Efic.</th><th>R$/km</th><th>Abast.</th></tr></thead>
                      <tbody>
                        {A.porMotorista.slice(0, 12).map((m) => (
                          <tr key={m.key}>
                            <td className="adv-tbl__name">
                              {m.nome}
                              {m.numVeiculos > 1 && <span className="adv-tbl__chip">{m.numVeiculos} veíc.</span>}
                              {m.estimados > 0 && (
                                <span className="adv-tbl__chip adv-tbl__chip--est" title={`${m.estimados} lançamento(s) atribuído(s) pelo motorista fixo do veículo`}>≈ {m.estimados} est.</span>
                              )}
                            </td>
                            <td>{BRL(m.gastoTotal)}</td>
                            <td className={m.eficiencia > 0 && m.eficiencia < A.kpis.eficienciaFrota ? 'adv-neg' : 'adv-pos'}>{KML(m.eficiencia)}</td>
                            <td>{m.custoPorKm > 0 ? BRL(m.custoPorKm) : '—'}</td>
                            <td>{m.abast}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {A.porMotorista.some((m) => m.estimados > 0) && (
                    <p className="adv-nota">≈ est. = gasto sem condutor no lançamento, atribuído ao motorista fixo do veículo.</p>
                  )}
                </>
              )}
            </Painel>
          </div>

          <div className="adv-grid-2">
            {/* ── [IDEIA] Custo por km ── */}
            <Painel titulo="Custo por km rodado" subtitulo="TCO operacional — quanto cada veículo custa por km" icon={<FaTachometerAlt />} tag="ideia">
              <BarrasH
                mono
                formato={BRL}
                dados={A.porVeiculo.filter((v) => v.custoPorKm > 0).sort((a, b) => b.custoPorKm - a.custoPorKm).slice(0, 10)
                  .map((v) => ({ key: v.veiculo_id, label: v.placa || v.modelo, valor: v.custoPorKm, cor: v.custoPorKm > A.kpis.custoPorKmFrota ? COR.danger : COR.estac }))}
              />
            </Painel>

            {/* ── [IDEIA] Comparativo de postos ── */}
            <Painel titulo="Comparativo de postos" subtitulo="Preço médio pago por litro em cada posto" icon={<FaMapMarkerAlt />} tag="ideia">
              <BarrasH
                formato={(v) => `${BRL(v)}/L`}
                dados={A.postos.filter((p) => p.precoMedioLitro > 0).sort((a, b) => b.precoMedioLitro - a.precoMedioLitro).slice(0, 10)
                  .map((p, i) => ({ key: i, label: p.posto, valor: p.precoMedioLitro, cor: PALETA_SERIE[i % PALETA_SERIE.length] }))}
              />
            </Painel>
          </div>

          {/* ── [IDEIA] Scatter eficiência × custo ── */}
          <Painel titulo="Quadrante de performance" subtitulo="Eficiência (km/L) × custo por km. Bolha maior = maior gasto. Canto superior esquerdo é o ideal." icon={<FaCrosshairs />} tag="ideia">
            <ScatterPerf pontos={A.scatter} />
          </Painel>

          <div className="adv-grid-2">
            {/* ── Distribuição combustível ── */}
            <Painel titulo="Gasto por combustível" subtitulo="Distribuição por tipo de combustível" icon={<FaGasPump />}>
              <Donut dados={A.tipoCombustivel} />
            </Painel>

            {/* ── Distribuição Sem Parar ── */}
            <Painel titulo="Sem Parar por tipo de uso" subtitulo="Pedágio, estacionamento, plano contratado" icon={<FaRoad />}>
              <Donut dados={A.tipoUsoSP} />
            </Painel>
          </div>

          {/* ── [IDEIA] Detector de anomalias ── */}
          <Painel
            titulo="Detector de anomalias"
            subtitulo="Odômetro regressivo, preço por litro fora da curva e consumo implausível"
            icon={<FaExclamationTriangle />}
            tag="ideia"
            badge={<span className={`adv-anom-count ${A.anomalias.length ? 'tem' : 'zero'}`}>{A.anomalias.length} encontrada(s)</span>}
          >
            {A.anomalias.length === 0 ? (
              <div className="adv-anom-ok"><FaLightbulb /> Nenhuma anomalia no período. Dados consistentes.</div>
            ) : (
              <div className="adv-scroll adv-anom-list">
                {A.anomalias.slice(0, 40).map((an, i) => (
                  <div key={i} className={`adv-anom adv-anom--${an.sev}`}>
                    <div className="adv-anom__head">
                      <span className="adv-anom__tag">{an.tipo === 'odometro' ? 'Odômetro' : an.tipo === 'preco' ? 'Preço/L' : 'Consumo'}</span>
                      <span className="adv-anom__placa">{an.placa}</span>
                      <span className="adv-anom__meta">{ymd(an.data)} · {an.motorista} · {BRL(an.valor)}</span>
                    </div>
                    <p className="adv-anom__detalhe">{an.detalhe}</p>
                  </div>
                ))}
              </div>
            )}
          </Painel>

        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponentes de layout
// ─────────────────────────────────────────────────────────────────────────────
function KpiCard({ icon, cor, label, valor, sub }) {
  return (
    <div className="adv-kpi" style={{ '--kpi-cor': cor }}>
      <div className="adv-kpi__icon">{icon}</div>
      <div className="adv-kpi__body">
        <span className="adv-kpi__label">{label}</span>
        <strong className="adv-kpi__valor">{valor}</strong>
        {sub && <span className="adv-kpi__sub">{sub}</span>}
      </div>
    </div>
  );
}

function Painel({ titulo, subtitulo, icon, tag, badge, children }) {
  return (
    <section className="adv-painel">
      <header className="adv-painel__head">
        <div className="adv-painel__title">
          {icon && <span className="adv-painel__icon">{icon}</span>}
          <div>
            <h3>{titulo}{tag === 'ideia' && <span className="adv-painel__ideia"><FaLightbulb /> ideia</span>}</h3>
            {subtitulo && <p>{subtitulo}</p>}
          </div>
        </div>
        {badge}
      </header>
      <div className="adv-painel__body">{children}</div>
    </section>
  );
}

function Legenda({ itens }) {
  return (
    <div className="adv-legenda">
      {itens.map((it, i) => (
        <span key={i} className="adv-legenda__item"><span className="adv-dot" style={{ background: it.cor }} />{it.txt}</span>
      ))}
    </div>
  );
}